<script lang="ts" setup>
import {
  useStorage,
  computed,
  useTemplateRef,
  watch,
  reactive,
  useState,
} from "#imports";
import { useElementSize } from "@vueuse/core";
import { isSSR } from "~/constants";
const { isResizable, formattedImageUrl } = useStorage();

const props = withDefaults(
  defineProps<{
    src?: string;
    width?: number;
    height?: number;
    quality?: number;
    resize?: string;
    lazyLoad?: "lazy" | "eager";
    forceResize?: boolean;
  }>(),
  {
    src: "/media/images/placeholder.webp",
    width: 200,
    height: 200,
    quality: 90,
    resize: "cover",
    overlay: false,
    lazyLoad: "lazy",
    forceResize: false,
  },
);

const isImageResizable = computed(() => isResizable(props.src));

const imgRef = useTemplateRef("img");
const { width: eleWidth, height: eleHeight, stop } = useElementSize(imgRef);

// this hacky key assures that the value is per component instance, rather than per file
const wasSSR = useState(`MPImg-isSSR-${JSON.stringify(props)}`, () => isSSR);

const imgDims = reactive<{ width: number; height: number }>({
  width: 0,
  height: 0,
});

watch([eleWidth, eleHeight], () => {
  if (eleWidth.value > 0 && eleHeight.value > 0) {
    stop();
    if (imgDims.width === 0 && imgDims.height === 0) {
      imgDims.width = Math.round(eleWidth.value);
      imgDims.height = Math.round(eleHeight.value);
    }
  }
});

const imageSrc = computed(() => {
  if (!isImageResizable.value) {
    return props.src;
  }
  if (wasSSR.value) {
    return formattedImageUrl(
      props.src,
      props.width,
      props.height,
      props.quality,
      props.resize,
    );
  }
  if (imgDims.width === 0 || imgDims.height === 0) {
    return undefined;
  }
  return formattedImageUrl(
    props.src,
    imgDims.width,
    imgDims.height,
    props.quality,
    props.resize,
  );
});

const imageSrcset = computed(() => {
  if (
    !isImageResizable.value ||
    (!wasSSR.value && (imgDims.width === 0 || imgDims.height === 0))
  ) {
    return undefined;
  }

  const sources = [] as string[];
  const width = wasSSR.value ? props.width : imgDims.width;
  const height = wasSSR.value ? props.height : imgDims.height;

  sources.push(
    formattedImageUrl(props.src, width, height, props.quality, props.resize),
  );
  sources.push(
    formattedImageUrl(
      props.src,
      Math.round(width * 1.5),
      Math.round(height * 1.5),
      props.quality,
      props.resize,
    ) + " 1.5x",
  );
  sources.push(
    formattedImageUrl(
      props.src,
      width * 2,
      height * 2,
      props.quality,
      props.resize,
    ) + " 2x",
  );

  return sources.join(", ");
});

const widthPx = computed(() => props.width + "px");
const heightPx = computed(() => props.height + "px");
</script>

<template>
  <img
    :class="{ cssResize: !isImageResizable && forceResize }"
    ref="img"
    :loading="props.lazyLoad"
    :src="imageSrc"
    :srcset="imageSrcset"
  />
</template>

<style lang="postcss" scoped>
img {
  max-width: 100%;
  display: block;
}

.cssResize {
  object-fit: cover;
  width: v-bind("widthPx");
  height: v-bind("heightPx");
  max-width: v-bind("widthPx");
  max-height: v-bind("heightPx");
}
</style>
